import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../../environment';
import { Config } from '../material-component/gym-config/gym-config.component';
import { Observable, of, map } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';
import {
  DEFAULT_SUBSCRIPTIONS_BEFORE_REQUIRED_DATA,
  MEMBER_REQUIRED_POLICY_TYPE,
  MEMBER_REQUIRED_SUBSCRIPTIONS_CONFIG,
  MemberRequiredFields,
} from '../models/member-required-fields.model';
import { memberRequiredFieldsFromConfigs } from '../models/utils';

@Injectable({
  providedIn: 'root',
})
export class GymConfigService {
  apiUrl = environment.apiUrl;
  private getGymConfigDataUrl = this.apiUrl + '/gym-config';
  private updateGymConfigUrl = this.apiUrl + '/gym-config/{id}';
  private getGymConfigByTypeUrl = this.apiUrl + '/gym-config/by-type';

  PAYMENT_METHOD_CONFIG = 'PAYMENT_METHOD';
  CASH_CONFIG_NAME = 'CASH';
  TRANSFER_CONFIG_NAME = 'TRANSFER';
  CARD_CONFIG_NAME = 'CARD';
  cashConfig: null | any = null;
  transferConfig: null | any = null;
  cardConfig: null | any = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toastr: ToastrService,
    private errorHandler: ErrorHandlerService,
  ) {
    this.toastr.toastrConfig.timeOut = 0;
  }

  getGymConfigData() {
    const httpOptions = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    };

    return this.http.get(this.getGymConfigDataUrl, httpOptions).pipe(
      tap((response: any) => {}),
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.handleError(error);
        return throwError(error.error);
      }),
    );
  }

  getPaymentMethodConfig(): Observable<any[]> {
    return this.getGymConfigByType(this.PAYMENT_METHOD_CONFIG).pipe(
      map((data: any[]) => {
        let config: any[] = [];

        const card = data.find(d => d?.name === this.CARD_CONFIG_NAME);
        if (card) config.push(card);

        const transfer = data.find(d => d?.name === this.TRANSFER_CONFIG_NAME);
        if (transfer) config.push(transfer);

        const cash = data.find(d => d?.name === this.CASH_CONFIG_NAME);
        if (cash) config.push(cash);

        return config;
      }),
      catchError(() => of([])),
    );
  }

  updateGymConfig(config: Config) {
    const httpOptions = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    };

    return this.http.put(this.updateGymConfigUrl.replace('{id}', config.id.toString()), config, httpOptions).pipe(
      tap((response: any) => {}),
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.handleError(error);
        return throwError(error.error);
      }),
    );
  }

  getGymConfigByType(type: string) {
    const params = new HttpParams().set('type', type);

    const httpOptions = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      params,
    };

    return this.http.get<any[]>(this.getGymConfigByTypeUrl, httpOptions);
  }

  getRequiredMemberFields(): Observable<MemberRequiredFields> {
    return this.getGymConfigByType('MEMBER_REQUIRED_FIELD').pipe(map(memberRequiredFieldsFromConfigs));
  }

  /** For backward compatibility, a preference lookup error keeps ticket printing enabled. */
  isSaleTicketPrintingEnabled(): Observable<boolean> {
    return this.getGymConfigByType('SALE').pipe(
      map(configs => {
        const config = configs.find(current => current?.name === 'SALE_TICKET_PRINTING');
        return config ? config.isEnabled === true : true;
      }),
      catchError(() => of(true)),
    );
  }

  getSubscriptionsBeforeRequiredMemberData(): Observable<number> {
    return this.getGymConfigByType(MEMBER_REQUIRED_POLICY_TYPE).pipe(
      map(configs => {
        const value = configs.find(config => config.name === MEMBER_REQUIRED_SUBSCRIPTIONS_CONFIG)?.integerValue;
        return Number.isInteger(value) && value >= 0 ? value : DEFAULT_SUBSCRIPTIONS_BEFORE_REQUIRED_DATA;
      }),
    );
  }
}
